import {Identifier} from "../common/Identifier";
import {Module} from "../common/Module";
import {Nullable} from "../common/Nullable";
import {AuthenticatedUser} from "../users/AuthenticatedUser";
import { isPrivileged, isPrivilegedByIdentifier, Privileges} from "../../config/Privileges";
import {SoftDeletable} from "../common/SoftDeletable";
import {ProjectShort} from "../projects/Project";
import {InvoiceMode} from "./InvoiceMode";
import {InvoiceType} from "./InvoiceType";
import {Dateable, TimePeriodable} from "../common/TimePeriodable";
import {AmountOwedable, AmountOwedApprovable, AmountOwedWithTax} from "./AmountOwedable";
import {Sensitive} from "../common/Sensitive";
import {MapByNum} from "../common/Map";
import {Nameable} from "../common/Nameable";
import {WorkingHours} from "../timesheet/WorkingHours";
import {Money, Percent} from "./Finance";
import {SalesTaxable,  SalesTaxSummary} from "./SalesTaxProp";
import {BillPayableState} from "./Payable";
import {ProjectStagePhaseId} from "../projects/ProjectStagePhase";
import {IdAndValue} from "../common/IdAndValue";
import {PerformanceApprovable} from "./Performanceable";
import {ContractorProp} from "../contractor/Contractor";
import {InvoiceItemType} from "./InvoiceItemType";
import {InvoiceMethod} from "./InvoiceMethod";
import {OutgoingInvoiceItemId} from "./OutgoingInvoiceItem";


/**
 * The internal name for the accounting functional area within the system.
 */
export const ACCOUNTING_AREA_TYPE_NAME = "ACCOUNTING";

/**
 * Defines the configuration and access control rules for the Outgoing Invoices module.
 * This module manages invoices sent to clients for services rendered.
 */
export const OutgoingInvoiceModule: Module = {
        identifier: 'outgoing_invoice',
        /**
         * Checks if the user has the required permission to perform an action within the outgoing invoice module.
         * 
         * @param authUser - The currently logged-in user.
         * @param privilege - The specific action or permission being checked (e.g., VIEW, EDIT).
         * @param privilegeIdentifier - An optional specific identifier for resource-based permission checks.
         * @returns True if the user is authorized, false otherwise.
         */
        isPrivileged: (authUser: Nullable<AuthenticatedUser>, privilege: string, privilegeIdentifier?: string) => {

            if (typeof privilegeIdentifier !== "undefined") {
                return isPrivilegedByIdentifier(authUser, privilegeIdentifier);
            } else {
                return isPrivileged(
                    authUser,
                    ACCOUNTING_AREA_TYPE_NAME,
                    OutgoingInvoiceModule.identifier,
                    privilege
                );
            }
        }
};


/**
 * A collection of specific permissions available for managing account books and invoices.
 * Extends the base system privileges with invoice-specific actions like finalizing or canceling.
 */
export const AccountBookPrivileges = {
    ...Privileges,
    /** Permission to mark an invoice as final, preventing further edits. */
    markFinal: "MARK_FINAL",
    /** Permission to void or cancel an existing invoice. */
    markCanceled: "MARK_CANCELED"
};

/**
 * Represents a document that can be billed, such as an invoice or a credit note.
 * This is the primary data structure for financial billing entities.
 * 
 * @typeParam idType - The data type used for the unique identifier of the billable entity.
 */
export interface Billable<idType> extends InvoiceNumberable, SoftDeletable, BillableByProject {
    /** The calendar year the invoice belongs to. */
    year: number
    /** Financial totals including net, gross, and taxes. */
    totals: BillSummable
    /** The formal date issued on the invoice. */
    invoiceDate: Date
    /** Defines how the invoice is processed (e.g., manual, automatic). */
    mode: InvoiceMode
    /** The category of the document (e.g., standard invoice, partial, final). */
    type: InvoiceType
    /** The timeframe of service or delivery covered by this bill. */
    period: TimePeriodable
    /** Individual line items or service groups included in the bill. */
    items: BasicBillableItem[]
    /** The current settlement status (e.g., open, paid, overdue). */
    payableState: BillPayableState
}

/**
 * Represents a method of billing associated with an invoicing workflow.
 *
 * This interface extends the base Billable interface to include a specific billing method.
 * It is used to define a model for handling billing processes tied to a particular invoicing method,
 * ensuring consistency in how billable entities interact with invoicing systems.
 *
 * @template idType The type of the unique identifier for the billable entity (e.g., string, number, etc.).
 */
export interface BillableMethod<idType> extends Omit< Billable<idType>, "items" > {
    method: InvoiceMethod
    items: OutgoingBasicBillableItem[]
}



/**
 * Provides a unique reference number for identifying an invoice.
 * Used for tracking and legal documentation.
 */
export interface InvoiceNumberable {
    /** The human-readable identifier for the invoice. */
    invoiceNumber: string
}

/**
 * Associates a billing entity with a specific project.
 * Ensures that financial records are correctly allocated to project budgets.
 */
export interface BillableByProject {
    /** The project to which this bill is linked. */
    project: ProjectShort
}

/**
 * Allows linking an invoice to a corresponding cancellation document.
 * Used for maintaining an audit trail when invoices are voided.
 */
export interface CanceledBillLinkable {
    /** Reference to the invoice that cancels this one, if applicable. */
    canceledBill: Nullable< IdAndValue<number, string> >
}

/**
 * A summary of financial totals for a billing document.
 * Aggregates all costs, taxes, and payments to provide a clear financial overview.
 */
export interface BillSummable extends AmountOwedable, AmountOwedWithTax {
    /** A breakdown of sales taxes grouped by their respective tax rates. */
    salesTaxByTax: MapByNum<SalesTaxSummary>
    /** Total amount of payments already received for this bill. */
    totalPayments: Sensitive<Money>
    /** The total calculated amount that must be paid, after taxes and adjustments. */
    finalAmountOwed: Sensitive<Money>
    /** The sum of all sales tax components. */
    totalSalesTaxAmount: Sensitive<Money>
    /** The remaining balance that has not yet been paid. */
    openAmountOwed: Sensitive< Money >
}

/**
 * Identifies a partial invoice within a series of bills for a single project.
 * Useful for progress billing or milestone-based payments.
 */
export interface PartialBillable {
    /** The sequence number of the partial invoice. */
    partialInvoiceNumber: number
}


/**
 * Represents a basic billable item within an invoice.
 * All billable entities in the system implement this interface.
 */
export interface BasicBillableItem extends Nameable {
    /** Unique identifier of the parent invoice. */
    invoiceId: Identifier<number>
    /** Financial totals specific to this line item. */
    totals: BillItemTotalable
    /** The classification of the item (e.g., work hours, materials, flat rate). */
    invoiceItemType: InvoiceItemType
}

/**
 * Represents a single line item or service entry within an invoice.
 * Contains the specific financial details for a portion of the total bill.
 */
export interface BillableItems extends BasicBillableItem {
    /** Extra expenses associated with this item that are passed to the client. */
    additionalCosts: Sensitive<Money>
    /** The percentage markup or fee applied as an additional cost. */
    additionalCostsPercent: Percent
}

/**
 * Represents billable items associated with an outgoing invoice.
 *
 * This interface extends both `BillableItems` and `OutgoingInvoiceItemId` to consolidate information about
 * individual items that are invoiced and their unique identifiers within outgoing invoices.
 * It serves as a comprehensive representation for items that are chargeable to a client or customer.
 *
 * Use this interface when working with features or modules requiring detailed tracking of billable items in invoices.
 */
export interface OutgoingBasicBillableItem extends BasicBillableItem, OutgoingInvoiceItemId {

}

/**
 * Represents a billable item that combines standard billable properties with an outgoing invoice identifier.
 */
export interface OutgoingBillableItems extends OutgoingBasicBillableItem, OutgoingInvoiceItemId, BillableItems {}

/**
 * Extends basic item totals to include time-tracking information.
 * Used for items where the cost is derived from hours worked.
 */
export interface BillItemTimeTotalable extends BillItemTotalable {
    /** The total number of hours logged for this item. */
   totalHours: WorkingHours
    /** Hours that were logged but intentionally excluded from the bill. */
    totalNotBilledHours: WorkingHours
}

/**
 * Detailed information for a specific time entry being billed.
 * Provides transparency on who did what work and at what rate.
 */
export interface WorkHoursBillableTimeItem extends AmountOwedable {
    /** The amount of time spent on the task. */
    workHours: WorkingHours
    /** The hourly price charged for the work. */
    timeRate: Sensitive<Money>
    /** The name of the person who performed the work. */
    employeeName: string
    /** A detailed log of the activities performed. */
    workDescription: string
    /** Importance or scaling factor applied to this work item. */
    weight: Percent
    /** Tax configuration for this specific service. */
    salesTax: SalesTaxable
    /** The exact start and end times for the work performed. */
    workTime: TimePeriodable< Dateable, Dateable >
    /** The professional designation of the worker for this task. */
    roleName: string
    /** internal ID of the worker's role. */
    roleId: number
    /** The number of hours that will be charged to the client. */
    billedHours: number
    /** The number of hours recorded but not charged. */
    notBilledHours: number
}

/**
 * Links a billable time item to a specific timesheet entry in the system.
 * Allows for deep-linking and auditing between invoices and original time logs.
 */
export interface WorkHoursBillableTimesheetItem extends WorkHoursBillableTimeItem {
    /** Unique identifier of the source timesheet. */
    timesheetId:Identifier<number> 
}

/**
 * A group of time-based work entries bundled together for billing.
 * Commonly used to represent a collection of tasks performed under a specific project phase.
 */
export interface WorkHoursBillable extends BillableItems {
    /** Aggregate time and financial totals for the group. */
    totals: BillItemTimeTotalable
    /** Individual work records included in this group. */
    timeItems: WorkHoursBillableTimeItem[]
}

/**
 * A group of work entries that are strictly linked to system timesheets.
 * Ensures all billed time corresponds to verified employee records.
 */
export interface WorkHoursTimesheetBillable extends Omit< WorkHoursBillable, 'timeItems' >{
    /** Individual timesheet-linked work records. */
    timeItems: WorkHoursBillableTimesheetItem[]
}

/**
 * Organizes billable work hours by specific project tasks and roles.
 * Provides the highest level of detail for client reporting.
 */
export interface WorkHoursBillableByTask extends WorkHoursTimesheetBillable, Omit< ProjectPhaseTaskBillable, "totals"> {
    /** Breakdown of time spent, grouped by employee roles. */
    roleTimeItems: BillableRoleTimeItem[]
    /** The name of the project phase this work belongs to. */
    phaseName: string
}

/**
 * Summarizes work hours and costs for a specific role within a project.
 * Useful for understanding labor distribution and costs per skill level.
 */
export interface BillableRoleTimeItem extends Nameable, AmountOwedable {
    /** The hourly rate applied for this specific role. */
    timeRate: Sensitive<Money>
    /** Total hours of this role that are being charged. */
    billedHours: WorkingHours
    /** Total hours of this role that are not being charged. */
    notBilledHours: WorkingHours
    /** Sales tax configuration for this role's services. */
    salesTax: SalesTaxable
    /** Unique identifier for this role-based grouping. */
    id: Identifier<string>
    /** Numeric ID of the role. */
    roleId: number
}

/**
 * Represents a billing item associated with a specific phase of a project.
 * Helps track financial progress against project milestones.
 */
export interface ProjectPhaseBillable extends OutgoingBillableItems, ProjectStagePhaseId {
    /** Identifier for the overall project stage. */
    stageId: Identifier<number>
}

/**
 * A project phase billed at a fixed price rather than by hour.
 * Revenue is often recognized based on the percentage of completion.
 */
export interface ProjectPhaseFlatRateBillable extends ProjectPhaseBillable {
    /** Percentage of work completed within the timeframe. */
    phasePerformanceInTime: Percent
    /** The total allocated budget for this phase. */
    phaseBudgetInTime: Sensitive< Money >
    /** The portion of the performance that is currently being billed. */
    billedPerformance: Percent
}

/**
 * Represents a billing item tied to a specific task within a project phase.
 * Allows for granular billing at the task level.
 */
export interface ProjectPhaseTaskBillable extends ProjectPhaseBillable {
    /** Identifier for the specific task being billed. */
    phaseTaskId: Identifier<number>
}

/**
 * A specific task billed at a flat rate.
 * Tracks progress billing for individual tasks.
 */
export interface ProjectPhaseTaskFlatRateBillable extends ProjectPhaseTaskBillable, ProjectPhaseFlatRateBillable {
    /** The completion percentage that was recorded in the previous invoice. */
    lastBilledPerformance: Percent
}

/**
 * Core financial data for any billable item.
 * Includes basic owed amounts and tax information.
 */
export interface BillItemTotalable extends AmountOwedable, AmountOwedWithTax {
    /** The sales tax applied to this item. */
    salesTax: SalesTaxable
}

/** CONTRACTOR / INCOMING INVOICES */

/**
 * Represents an invoice received from a third-party contractor.
 * Used for managing outgoing payments and project expenses.
 * 
 * @typeParam idType - The data type used for the unique identifier of the contractor bill.
 */
export interface ContractorBillable<idType> extends Omit< Billable<idType>, "totals" | "items" >, ContractorProp {
    /** Financial totals, including approval state for payment. */
    totals: ContractorBillSummable
    /** List of tasks or services provided by the contractor. */
    items: ContractorPhaseTaskBillable[]
}

/**
 * Financial summary for a contractor invoice, including internal approval details.
 * Ensures that only verified and approved amounts are processed for payment.
 */
export interface ContractorBillSummable extends BillSummable, AmountOwedApprovable {
    /** The total amount that has been formally approved for payment, including taxes. */
    approvedAmountOwedWithTax: Sensitive<Money>
}

/**
 * A specific service or task provided by a contractor that is being billed.
 * Maps contractor work directly to project phases and tasks for cost tracking.
 */
export interface ContractorPhaseTaskBillable extends BillableItems,PerformanceApprovable {
    /** ID of the project phase. */
    phaseId: Identifier<number>
    /** ID of the project task. */
    taskId: Identifier<number>
    /** ID of the project stage. */
    stageId: Identifier<number>
    /** The performance level achieved by the contractor in this phase. */
    phasePerformanceInTime: Percent
    /** The agreed budget for the contractor's work in this phase. */
    phaseBudgetInTime: Sensitive<Money>
    /** The portion of the contractor's performance being billed now. */
    billedPerformance: Percent

    /** Financial totals for this contractor item. */
    totals: ContractorItemTotalable
}

/**
 * Detailed financial and approval status for a single contractor billing item.
 * Tracks how much of the requested amount has been sanctioned by the project manager.
 */
export interface ContractorItemTotalable extends BillItemTotalable, AmountOwedApprovable {
    /** Net amount approved for this specific item, including tax. */
    approvedAmountOwedWithTax: Sensitive<Money>
    /** The portion of sales tax that corresponds to the approved amount. */
    approvedAmountSalesTaxAmount: Sensitive<Money>

    /** The percentage of the item's total value that has been approved. */
    approvedPercentage: Percent
}